import _mapValues from 'lodash/mapValues';
import {
  AppRouteParams,
  AppRouteQueryParams,
  RouteConfig,
  RouteDef,
} from 'common/src/modules/routing/routing.types';
import {Logger} from '../../../copilot.types';

function buildAppRoute({
  routeDef,
  pathParamValues,
  queryParamValues,
  logger,
}: {
  routeDef: RouteDef;
  pathParamValues: AppRouteParams;
  queryParamValues: AppRouteQueryParams;
  logger: Logger;
}): RouteConfig {
  const missingPathParamValues = _getMissingParamsWithDefaultValues(
    routeDef.pathParams,
    pathParamValues,
  );

  if (Object.keys(missingPathParamValues).length > 0) {
    logger.log('Adding missing path param values:', missingPathParamValues);
  }

  const missingQueryParamValues = _getMissingParamsWithDefaultValues(
    routeDef.queryParams,
    queryParamValues,
  );

  const finalPathParamValues = {
    ...pathParamValues,
    ...missingPathParamValues,
  };

  if (Object.keys(missingQueryParamValues).length > 0) {
    logger.log('Adding missing query param values:', missingQueryParamValues);
  }

  const flattenedQueryParamValues = _mapValues(queryParamValues, (value) =>
    Array.isArray(value) ? value.join(',') : String(value),
  );
  const finalQueryParamValues = {
    ...flattenedQueryParamValues,
    ...missingQueryParamValues,
  };

  const appRoute: RouteConfig = {
    url: routeDef.path,
    ...(Object.keys(finalPathParamValues).length > 0 ? {pathParams: finalPathParamValues} : {}),
    ...(Object.keys(finalQueryParamValues).length > 0 ? {queryParams: finalQueryParamValues} : {}),
  };

  return appRoute;
}

// Return an object containing all parameters that appear in paramDefs with a default value
// and do not appear in existingValues.
function _getMissingParamsWithDefaultValues<T extends {key: string; defaultValue?: string}>(
  paramDefs: T[],
  existingValues: Record<string, unknown>,
): AppRouteParams {
  return Object.fromEntries(
    paramDefs
      .filter((param) => !(param.key in existingValues) && param.defaultValue !== undefined)
      .map((param) => [param.key, param.defaultValue as string | number]),
  );
}

export default buildAppRoute;
